import { ArrowDownTrayIcon } from "@heroicons/react/20/solid";
import {
  ArrowPathIcon,
  ArrowRightStartOnRectangleIcon,
  BackwardIcon,
  DocumentCheckIcon,
  PaperAirplaneIcon
} from "@heroicons/react/24/outline";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Outlet,
  useLoaderData,
  useNavigate,
  useOutletContext,
  useParams,
  useLocation
} from "react-router-dom";
import { CollapsibleSidebar as CollapsibleSidebarUnified } from "../../../unified-dashboard/src/components/atomic/CollapsibleSidebar";
import ApproveLoanIcon from "../../assets/icons/approve_loan.svg";
import CancelApplicationIcon from "../../assets/icons/cancel_application.svg";
import RejectLoanIcon from "../../assets/icons/reject_loan.svg";
import type { BreadCrumb } from "../../components/atomic/Breadcrumbs/interface";
import { CollapsibleSidebar } from "../../components/atomic/CollapsibleSidebar";
import DialogOne from "../../components/atomic/Dialog/DialogOne";
import PageHeader from "../../components/atomic/Header/PageHeader";
import { TabsOne } from "../../components/atomic/Tabs";
import ActionButton from "../../components/composed/ActionButton";
import ApproveKYCModal from "../../components/composed/ApproveKYCModal";
import CancelLoanApplicationModal from "../../components/composed/CancelLoanApplicationModal";
import ConfirmationModal from "../../components/composed/ConfirmationModal";
import DetailsSidebar from "../../components/composed/DetailsSidebar/DetailsSidebar";
import DisqualifyLoanApplicationModal from "../../components/composed/DisqualifyLoanApplicationModal";
import GenerateLinkModal from "../../components/composed/GenerateLinkModal";
import PrivateComponent from "../../components/composed/PrivateComponent";
import SendSignAgreementModal from "../../components/composed/SendSignAgreementModal";
import { CommonService } from "../../services/Common";
import { LoanApplicationService } from "../../services/LoanApplication";
import { LoanApplicationService as LoanApplicationServiceUnified } from "../../../unified-dashboard/src/services/LoanApplication";
import { notify } from "../../shared/context/AlertContext";
import GetAuthContext from "../../shared/context/AuthContext";
import { useLoanApplicationNavbar } from "../../shared/context/LoanApplicationNavbarContext";
import GetMixpanelContext from "../../shared/context/MixpanelContext";
import type { CustomLoaderFunctionArgs } from "../../shared/router/RouteLoader";
import { Get, Post } from "../../shared/services/Fetch";
import { LOAN_STATUS_CONFIG, LOAN_WORKFLOW_STATE_CONFIG } from "../../shared/utils/Common";
import { ACTIONS_LIST, ORG_IDS } from "../../shared/utils/constants";
import { downloadLink } from "../../shared/utils/DateUtil";
import {
  getTabsByProductType,
  noHeadersResponse
} from "../../shared/utils/Global";
import SliderContainer from "../LoanTermDeviation/SliderContainer";
import Notes from "./components/Notes";
import {
  loanDetailFields,
  loanDetailSidebarFields,
  SIDEBAR_BUTTON_KEYS
} from "./constant";
import WaitStateBanner from "../../../unified-dashboard/src/components/composed/LoanApplication/WaitStateBanner";
import FloatingNotesButton from "../../../unified-dashboard/src/components/composed/LoanApplication/FloatingNotesButton";
import { useDerivedTabsFromUiPermissions } from "../../../unified-dashboard/src/shared/hooks/useDerivedTabsFromUiPermissions";
import NextStage from "../../../unified-dashboard/src/components/composed/MoveApplication/NextStage";
import CaseMovedModal from "@unified-dashboard/src/components/composed/CaseMovedModal/CaseMovedModal";
import { useTabsFromUIConfig } from "@unified-dashboard/src/shared/hooks/useTabsFromUIConfig";
import DynamicTabRenderer from "../../../unified-dashboard/src/components/composed/DynamicTabRenderer";
import { useRenderStrategy } from "@unified-dashboard/src/shared/hooks/useRenderStrategy";
import UiConfigGate from "@unified-dashboard/src/components/composed/UiConfigGate";
import { canonicalizeKey } from "@unified-dashboard/src/core/access-control";
import { underscoreToSpace } from "@unified-dashboard/src/shared/utils/TextUtil";

interface SidebarButtonConfig {
  isVisible?: boolean;
  visible?: { isVisible?: boolean };
}


function isSidebarButtonVisible(
  buttons: Record<string, SidebarButtonConfig | undefined> | undefined,
  buttonKey: string
): boolean {
  if (!buttons) return false;

  const canonicalKey = canonicalizeKey(buttonKey);
  const button =
    buttons[buttonKey] ??
    Object.entries(buttons).find(
      ([key]) => canonicalizeKey(key) === canonicalKey
    )?.[1];

  return !!(button?.isVisible ?? button?.visible?.isVisible);
}
import { useIsHFCL } from "@unified-dashboard/src/shared/hooks";
import { isMyQueueEnabledOrg } from "@unified-dashboard/src/shared/utils/Global";

export interface LoanApplicationDetailsContext {
  whiteLabelStyle: any;
  reloadLoanDetails: Function;
  loanDetails: any;
  hasMorePages: boolean;
  workflowLoaderData: any;
  reloadWorkflowDetails: Function;
  selectedCoApplicantUserID: any;
  setSelectedCoApplicantUserID: any;
  uiPermissions?: {
    tabs: {
      [key: string]: {
        isVisible?: boolean;
      };
    };
    sidebar: {
      buttons: {
        [key: string]: {
          isVisible?: boolean;
        };
      };
    };
  };
}

export async function _getNotes(
  loanID: string,
  token: string,
  pageName: string
) {
  const headers = new Headers();
  if (pageName) {
    headers.append("page-header", pageName);
  } else {
    return noHeadersResponse();
  }
  return await Get(
    `/masterDashboard/notes?loanApplicationID=${loanID}`,
    token,
    headers
  );
}

export async function loanApplicationDetailsLoader({
  params,
  getToken,
  pageName
}: CustomLoaderFunctionArgs) {
  const token = getToken();
  const { loanID, sourceEntityID = "" } = params;

  const promises = await Promise.all([
    await LoanApplicationService.getWaitState({
      token,
      loanApplicationID: loanID as string,
      pageName
    }),
    await LoanApplicationService.getLoanDetails({
      loanApplicationID: loanID as string,
      token,
      pageName
    }),
    await LoanApplicationService.getNotes({
      loanApplicationID: loanID as string,
      token,
      pageName
    }),
    await LoanApplicationService.getLoanOffer({
      loanApplicationID: loanID as string,
      token,
      pageName
    }),
    await LoanApplicationService.getOfferNegotiationWFStatus({
      loanApplicationID: loanID as string,
      token,
      pageName
    }),
    await LoanApplicationService.getWorkflows({
      loanApplicationID: loanID as string,
      token,
      pageName
    })
  ]);

  return await [...promises, _getNotes];
}

function LoanApplicationDetails({ pageName }: any) {
  const outletContxt = useOutletContext() as any;
  const { whiteLabelStyle = {} } = outletContxt ?? {};

  const { sourceEntityID, loanID, "*": tail } = useParams();
  const { setCurrentScreen, trackUserAction } = GetMixpanelContext();
  const { setLoanData } = useLoanApplicationNavbar();
  const [
    { data: waitStateLoaderData },
    { data: loanDetailsLoaderData, status, error },
    { data: notesLoaderData },
    { data: loanOfferDetails },
    { data: loanTermDeviationStatus },
    { data: workflowLoaderData },
    _getNotes
  ] = useLoaderData() as any;

  const [waitStateData, setWaitStateData] = useState(
    waitStateLoaderData ?? null
  );
  const [loanDetailsData, setLoanDetailsData] = useState(loanDetailsLoaderData);
  const [waitLoading, setWaitLoading] = useState(false);
  const [notesData, setNotesData] = useState(notesLoaderData);
  const [isGenerateLinkModalOpen, setIsGenerateLinkModalOpen] =
    useState<boolean>(false);
  const [webLink, setWebLink] = useState<string>("");
  const [notes, setNotes] = useState<any[]>(notesData?.notes ?? []);
  const [showMoveApplicationToNextStage, setShowMoveApplicationToNextStage] =
    useState<boolean>(false);
  const [showApproveKYCModal, setShowApproveKYCModal] =
    useState<boolean>(false);
  const [showDisqualifyLoanModal, setShowDisqualifyLoanModal] =
    useState<boolean>(false);
  const [showCancelLoanApplicationModal, setShowCancelLoanApplicationModal] =
    useState<boolean>(false);

  const [isNotesOpen, setIsNotesOpen] = useState<boolean>(false);

  const [bcRange, setBcRange] = useState(null);
  const [bcStatus, setBcStatus] = useState(null);
  const [showBankConnect, setShowBankConnect] = useState(false);
  const [showSendSignAgreementModal, setShowSendSignAgreementModal] =
    useState(false);
  const { fetchToken, userState, getPageConfig, setUserState } =
    GetAuthContext();
  const { permissions } = userState;
  const pageConfig = getPageConfig();
  const isMobile = window.innerWidth < 640;
  const { loanDetails = {} } = loanDetailsData ?? {};
  const [workflowData, setWorkflowData] = useState(workflowLoaderData);

  const [isSliderOpen, setIsSliderOpen] = useState<boolean>(false);
  const [currentActiveSlider, setCurrentActiveSlider] = useState<string>("");
  const [sliderBack, setSliderBack] = useState<boolean>(false);
  const [timelineInfo, setTimelineInfo] = useState<any>();
  const [refetchLoanTerm, setRefetchLoanTerm] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loanDeviationPageName, setLoanDeviationPageName] =
    useState<string>("");
  const [isLoanDeviationLender, setIsLoanDeviationLender] =
    useState<boolean>(false);
  const [loanDeviationInfo, setLoanDeviationInfo] = useState<any>();
  const navigate = useNavigate();
  const [showCaseMovedModal, setShowCaseMovedModal] = useState<boolean>(false);
  const [derivedTabs, setDerivedTabs] = useState<any[]>([]);

  const isABFLBL = pageConfig?.global?.isABFL ?? false;
  const isABFLPL = pageConfig?.global?.isABFLPL ?? false;

  const isMflLap = userState?.organizationID === ORG_IDS.MFL_LAP;
  const isMFL = userState?.organizationID === ORG_IDS.MFL;
  const isJIO = userState?.organizationID === ORG_IDS.JIO;

  const tabsAndSidebarConfig = useDerivedTabsFromUiPermissions(
    loanID as string,
    fetchToken(),
    loanDetailsData?.lenderID || "",
    true,
    pageName
  );

  const tabsFromUIConfig = useTabsFromUIConfig({
    token: fetchToken(),
    uniqueID: loanDetailsData?.organizationID || "",
    isLenderDashboard: false,
    loanID,
    userID: loanDetailsData.userID,
    pageName: "Loan Application Detail Page"
  });


  const { isConfigDrivenTabContent, isConfigDrivenTabs, isConfigDrivenSidebar } =
    useRenderStrategy();

  let activeTab;
  if (Array.isArray(derivedTabs)) {
    activeTab = derivedTabs.find(t => t.route === `/${tail?.split('/')[0]}`);
  }

  useEffect(() => {
    if (isConfigDrivenTabs && !tail && derivedTabs?.length > 0) {
      const firstTab = [...derivedTabs].sort((a, b) => a.position - b.position)[0];
      const firstTabPath = firstTab ? firstTab.route : "details";

      navigate(`/loan-applications/details/${sourceEntityID}/${loanID}/${firstTabPath}`, {
        replace: true
      });
    }
  }, [tail, derivedTabs, loanID, navigate]);


  const checkSidebarButtonVisibility = useCallback(
    (buttonKey: string) =>
      isMflLap
        ? isSidebarButtonVisible(tabsAndSidebarConfig.sidebar?.buttons, buttonKey)
        : isConfigDrivenSidebar
          ? isSidebarButtonVisible(tabsFromUIConfig.sidebar?.buttons, buttonKey)
          : false,
    [isMflLap, isConfigDrivenSidebar, tabsAndSidebarConfig, tabsFromUIConfig]
  );


  const isActionVisible = (buttonKey: string, isShow: unknown) => {
    if (isConfigDrivenSidebar) {
      return !!isShow && !!checkSidebarButtonVisibility(buttonKey);
    }
    return !!isShow;
  };

  useEffect(() => {
    if (!loanID) return;
    if (isConfigDrivenTabs) {
      setDerivedTabs(tabsFromUIConfig?.tabs);
    }
  }, [loanID, tabsFromUIConfig]);

  useEffect(() => {
    if (!loanID || !isMflLap) return;

    if (tabsAndSidebarConfig.tabs?.length) {
      setUserState((prevState) => ({
        ...prevState,
        uiDrivenTabs: tabsAndSidebarConfig.tabs
      }));
    }
  }, [
    loanID,
    isMflLap,
    tabsAndSidebarConfig.tabs,
    setUserState,
    userState?.organizationID
  ]);

  const currentActiveSliderHandler = useCallback(
    (
      slider: string,
      info: any = {},
      nested = false,
      isLenderDashboard: boolean = false,
      sliderPageNameParam: string = "Loan Application Loan Term Deviation Tab"
    ) => {
      if (!isSliderOpen) setIsSliderOpen(true);

      setCurrentActiveSlider(slider);
      setLoanDeviationPageName(sliderPageNameParam);
      setIsLoanDeviationLender(isLenderDashboard);
      setLoanDeviationInfo(info);
      setTimelineInfo(info);
    },
    [isSliderOpen]
  );

  useEffect(() => {
    setCurrentScreen("Loan Application Details Page", {
      sourceEntityID,
      loanApplicationID: loanID
    });
    getBankConnectData();
  }, []);

  const updateNavbarLoanData = useCallback(
    (loanDataObj: any) => {
      if (loanDataObj?.loanDetails) {
        const applicantName = loanDataObj?.loanDetails?.name || "";
        const loanApplicationID = loanDataObj?.loanApplicationID || "";
        const loanApplicationNum = loanDataObj?.loanApplicationNum || "";

        if (applicantName || loanApplicationID) {
          setLoanData({
            applicantName,
            loanApplicationID,
            loanApplicationNum
          });
        }
      } else setLoanData(null);
    },
    [setLoanData]
  );

  useEffect(() => {
    updateNavbarLoanData(loanDetailsData);

    return () => {
      setLoanData(null);
    };
  }, [loanDetailsData, updateNavbarLoanData, setLoanData]);

  const getWaitStateforABFL = async (
    resourceName = "check_abfl_lan_retrigger"
  ) => {
    const payload = {
      resourceName,
      sectionName: "default",
      loanApplicationID: loanID,
      userID: loanDetailsData?.userID
    };
    const response = await LoanApplicationService.getWaitStateforABFL({
      token: fetchToken(),
      payload,
      pageName
    });

    if (response.data?.data?.lanRetriggerStatus === true) {
      setWaitStateData({
        waitState: true,
        suggestedAction:
          "Please Retrigger ABFL LMS API or raise a support ticket in techsupport@finbox.in if issue persists",
        suggestedActionType: "Retrigger"
      });
    } else {
      setWaitStateData({});
    }
    setWaitLoading(false);
  };

  useEffect(() => {
    if (
      loanDetailsData?.userID &&
      ORG_IDS.ABFL_PL === loanDetailsData?.organizationID
    ) {
      getWaitStateforABFL();
    }
  }, [loanDetailsData?.userID]);

  async function reloadWaitState() {
    if (loanID) {
      const response = await LoanApplicationService.getWaitState({
        token: fetchToken(),
        loanApplicationID: loanID,
        pageName
      });

      if (response.status) {
        setWaitStateData(response.data);
      }
    }
  }

  const resolveWaitState = async (endpoint) => {
    setWaitLoading(true);
    try {
      if (ORG_IDS.ABFL_PL === loanDetailsData?.organizationID) {
        getWaitStateforABFL("abfl_lan_retrigger");
        return;
      }
      const headers = new Headers();
      if (pageName) {
        headers.append("page-header", pageName);
      } else {
        return noHeadersResponse();
      }
      const data = await Get(
        `/masterDashboard/${endpoint}?loanApplicationID=${loanID}`,
        fetchToken(),
        headers
      );
      if (data.status) {
        const status = await pollWaitState();
        if (status === "completed") {
          await reloadLoanDetails();
          await reloadWaitState();
        }
      } else if (data.error) {
        notify({
          title: "Error",
          text: data.error
        });
      } else {
        notify({
          title: "Error",
          text: "Failed resolve issue. Please Contact FinBox Team"
        });
      }
    } catch (error) {
      notify({
        title: "Error",
        text: "Failed resolve issue. Please Contact FinBox Team"
      });
    }
    setWaitLoading(false);
  };

  const pollWaitState = async () => {
    let count = 0;
    const MAX_COUNT = 20;
    const callWaitState = async (
      resolve: (msg: "timeout" | "completed") => void,
      reject: () => void
    ) => {
      setTimeout(async () => {
        if (count >= MAX_COUNT) {
          return resolve("timeout");
        }
        if (waitStateData?.waitState) {
          await reloadWaitState();
          count = count + 1;
          callWaitState(resolve, reject);
        } else {
          return resolve("completed");
        }
      }, 3000);
    };
    return new Promise(callWaitState);
  };

  async function getBankConnectData() {
    const token = fetchToken();

    const [statusResponse, rangeResponse] = await Promise.all([
      LoanApplicationService.getBcRange({
        sourceEntityID: sourceEntityID!,
        token,
        pageName
      }),
      LoanApplicationService.getBcStatus({
        userID: loanDetailsData?.userID,
        token,
        pageName
      })
    ]);

    if (statusResponse.status) {
      setBcStatus(statusResponse.data);
    }

    if (rangeResponse.status) {
      setBcRange(rangeResponse.data);
    }
  }

  const pollBcStatus = async () => {
    setTimeout(async () => {
      const response = await LoanApplicationService.getBcStatus({
        userID: loanDetailsData?.userID,
        token: fetchToken(),
        pageName
      });
      if (response.status) {
        setBcStatus(response.data);
      } else {
        setBcStatus(null);
      }
      if (response.data.status === "processing") pollBcStatus();
      else window.location.reload();
    }, 3000);
  };

  const openBankConnect = () => {
    if (bcStatus && bcRange && loanDetailsData?.userID) {
      setShowBankConnect(true);
    }
  };

  const generateLink = useCallback(async () => {
    const params: any = {
      userID: loanDetailsData?.userID,
      token: fetchToken(),
      pageName,
      redirectURL: window.location.href
    };
    if (loanDetailsData?.programID && loanDetailsData?.programName) {
      params["program"] = loanDetailsData?.programName;
    }
    const response = await CommonService.generateLink(params);
    if (response.status) {
      trackUserAction("resume_journey", {
        loanID,
        userID: loanDetailsData?.userID || "",
        webLink: response?.data?.url || ""
      });
      setIsGenerateLinkModalOpen(true);
      setWebLink(response.data?.url || "");
    } else {
      notify({
        title: "Error",
        text: response.error
      });
    }
  }, [
    loanDetailsData?.userID,
    loanDetailsData?.programID,
    loanDetailsData?.programName,
    fetchToken,
    pageName,
    loanID,
    trackUserAction
  ]);

  async function getSignedAgreement() {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    const response = await Get<string>(
      `/masterDashboard/getSignedAgreement?loanApplicationID=${loanID}`,
      fetchToken(),
      headers
    );
    if (response.status) {
      trackUserAction("get_signed_agreement", {
        loanApplicationID: loanID,
        link: response.data
      });
      const link = response.data;
      const a = document.createElement("a");
      document.body.appendChild(a);
      a.style.cssText = "display: none";
      a.href = link;
      a.click();
      document.body.removeChild(a);
    } else {
      notify({
        text: response.error,
        title: "Error"
      });
    }
  }

  const getSoA = async () => {
    try {
      const headers = new Headers();
      if (pageName) {
        headers.append("page-header", pageName);
      } else {
        return noHeadersResponse();
      }
      const response = await Get<{ exportLink: string }>(
        `/masterDashboard/exportLedger?loanApplicationID=${loanID}`,
        fetchToken(),
        headers
      );
      if (response.status) {
        trackUserAction("get_soa", {
          loanApplicationID: loanID,
          link: response.data?.exportLink || ""
        });
        const link = response.data.exportLink;
        downloadLink(link);
      }
    } catch (e) {
      notify({
        text: "Network Error",
        title: "Error",
        type: "error"
      });
    }
  };

  async function getUnsignedAgreement() {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    headers.append("Content-Type", "text/html");
    const response = await Get<string>(
      `/masterDashboard/getUnsignedAgreement?loanApplicationID=${loanID}`,
      fetchToken(),
      headers
    );
    if (response.status) {
      const newWindow = window.open();
      newWindow?.document.write(response.data);
      trackUserAction(`unsigned_agreement`, {
        loanApplicationID: loanID
      });
    } else {
      notify({
        text: response.error,
        title: "Error"
      });
    }
  }
  const getAuthLetter = async () => {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    const response = await Get<string>(
      `/masterDashboard/getAuthLetter?loanApplicationID=${loanID}`,
      fetchToken(),
      headers
    );

    if (response.status) {
      trackUserAction("get_auth_letter", {
        loanApplicationID: loanID,
        link: response.data || ""
      });
      const link = response.data;
      if (link) {
        downloadLink(link);
      }
    } else {
      notify({
        text: response.error,
        title: "Error"
      });
    }
  };

  async function reloadNotesList() {
    try {
      const response = await _getNotes(loanID, fetchToken(), pageName);
      if (response.status) {
        if (response.data.notes) {
          setNotes(response.data.notes);
        }
      } else {
        notify({
          title: "Error",
          text: response.error
        });
      }
    } catch (error: any) {
      notify({
        title: "Error",
        text: new Error(error.toString()).message
      });
    }
  }

  async function onAddNote({ note }: any) {
    try {
      const headers = new Headers();
      if (pageName) {
        headers.append("page-header", pageName);
      } else {
        return noHeadersResponse();
      }
      const response = await Post(
        `/masterDashboard/addNote`,
        fetchToken(),
        {
          note,
          loanApplicationID: loanDetailsData?.loanApplicationID
        },
        headers
      );

      if (response.status) {
        trackUserAction("add_note", {
          loanApplicationID: loanID,
          note: note || ""
        });
        await reloadNotesList();
      } else {
        notify({
          title: "Error",
          text: response.error
        });
      }
    } catch (error: any) {
      notify({
        title: "Error",
        text: new Error(error.toString()).message
      });
    }
  }

  const reloadLoanDetails = useCallback(async () => {
    const response = await LoanApplicationService.getLoanDetails({
      loanApplicationID: loanID as string,
      token: fetchToken(),
      pageName
    });

    if (response.status) {
      setLoanDetailsData(response?.data);
      updateNavbarLoanData(response?.data);

      return response;
    } else {
      notify({
        title: "Error",
        text: response.error
      });
    }
  }, [loanID, fetchToken, pageName, updateNavbarLoanData]);

  const revokeDeleteRequest = useCallback(async () => {
    const response = await LoanApplicationService.revokeDeleteRequest({
      userID: loanDetailsData?.userID,
      token: fetchToken(),
      pageName
    });

    if (response.status) {
      trackUserAction("revoke_delete_request", {
        loanApplicationID: loanID,
        userID: loanDetailsData?.userID
      });

      reloadLoanDetails();
    } else {
      notify({
        title: "Error",
        text: response.error
      });
    }
  }, [
    loanDetailsData?.userID,
    fetchToken,
    pageName,
    trackUserAction,
    loanID,
    reloadLoanDetails
  ]);

  const reloadWorkflowDetails = async () => {
    const response = await LoanApplicationService.getWorkflows({
      loanApplicationID: loanID as string,
      token: fetchToken(),
      pageName
    });

    if (response.status) {
      setWorkflowData(response?.data);
      return response;
    } else {
      notify({
        title: "Error",
        text: response.error
      });
    }
  };

  const prependBankConnectItem = (items: any) => {
    const bankConnectData = {
      name: "Bank Connect",
      key: "bankConnect"
    };

    let value = "";

    switch (bcStatus?.status) {
      case "not_started":
        value = (
          <button
            className="text-indigo-600 text-xs font-medium"
            onClick={openBankConnect}
          >
            Upload Bank Statements
          </button>
        );
        break;

      case "failed":
        value = (
          <button
            className="text-indigo-600 text-xs font-medium"
            onClick={openBankConnect}
          >
            Retry Uploading Bank statements{" "}
            <span className="block">{bcStatus?.failureReason}</span>
          </button>
        );
        break;

      case "processing":
        value = (
          <button className="text-indigo-600 text-xs font-medium">
            Analysing Bank Statements
          </button>
        );
        break;

      default:
        break;
    }
    if (value) {
      bankConnectData.value = value;
    }
    return [bankConnectData, ...items];
  };

  async function approveBusinessKYC() {
    const response = await LoanApplicationService.approveBusinessKYC({
      loanApplicationID: loanID as string,
      token: fetchToken(),
      pageName
    });

    if (response.status) {
      notify({
        title: "Success",
        text: "Business KYC Approved",
        type: "success"
      });
      navigate(0);
    } else {
      notify({
        title: "Error",
        text: response.error || "Business KYC Approval Failed"
      });
    }
  }

  const getVisibleKeys = () => {
    let visibleActionKeys: string[] = [];
    let isApiStackUser = loanDetailsData?.loanDetails?.source !== "API_STACK_USER" ? true : false;

    if (!isApiStackUser) {
      if (
        [ORG_IDS.ABFL, ORG_IDS.ABFL_PL].includes(
          loanDetailsData?.organizationID
        )
      ) {
        isApiStackUser = false;
        visibleActionKeys = ["resumeJourney"];
      }

      if (loanDetailsData?.organizationID === ORG_IDS.IIFL) {
        isApiStackUser = false;
        visibleActionKeys = [
          "resumeJourney",
          "approveKYC",
          "rejectLoan",
          "authLetter",
          "signedAgreement",
          "statementOfAccounts",
          "unsignedAgreement"
        ];
      }
    }

    return {
      isApiStackUser,
      visibleActionKeys
    };
  };

  const { isApiStackUser, visibleActionKeys = [] } = getVisibleKeys();

  const isAllowedToApproveKYC =
    ["KYC_PROCESSING", "KYC_REJECTED"].includes(loanDetailsData?.status) &&
      visibleActionKeys?.includes("approveKYC")
      ? true
      : isApiStackUser;
  // This feature was implemented for Khatabook. Since they are no longer a customer, this is commented out
  // const isApproveLoan =
  //   !["DISBURSED", "LOAN_REJECTED", "CLOSED", "CANCELLED"].includes(
  //     loanDetailsData.status
  //   ) && loanDetailsData.status === "KYC_SUCCESS";
  const isRejectLoan =
    !["DISBURSED", "LOAN_REJECTED", "CLOSED", "CANCELLED"].includes(
      loanDetailsData?.status
    ) && visibleActionKeys?.includes("rejectLoan")
      ? true
      : isApiStackUser || isMFL;

  const CANCLE_STATUS = ["DISBURSED", "LOAN_REJECTED", "CLOSED", "CANCELLED"];

  const cancelStatusCondition = pageConfig?.global?.isMFL
    ? [...CANCLE_STATUS]
    : [...CANCLE_STATUS, "SIGN_AGREEMENT"];

  const canCancelApplication =
    !cancelStatusCondition.includes(loanDetailsData?.status) &&
    (isApiStackUser || isMFL || isJIO);
  const canDownloadAuthLetter =
    ["BANK_ADDED", "SIGN_AGREEMENT", "DISBURSED"].includes(
      loanDetailsData?.status
    ) && visibleActionKeys?.includes("authLetter")
      ? true
      : isApiStackUser;

  const canDownloadSignAgreement =
    ["SIGN_AGREEMENT", "DISBURSED", "CLOSED"].includes(
      loanDetailsData?.status
    ) && visibleActionKeys?.includes("signedAgreement")
      ? true
      : isApiStackUser;

  const canGetSoA =
    ["DISBURSED", "CLOSED"].includes(loanDetailsData?.status) &&
    ["personal_loan", "business_loan"].includes(loanDetailsData?.loanType);

  const canSendSignAgreementToLender =
    loanDetailsData?.showSignAgreementButton && isApiStackUser;
  const showApproveBusinessKYC = workflowLoaderData?.workflows?.some(
    (workflow) =>
      workflow.workflowName === "abfl_kyc_wf" &&
      !["approved", "rejected"].includes(workflow.status) &&
      workflow.actions["abfl_approve_business_docs"] === true
  );

  const showUnsignedAgreement =
    ["BANK_ADDED", "SIGN_AGREEMENT", "DISBURSED", "CLOSED"].includes(
      loanDetailsData?.status
    ) && visibleActionKeys?.includes("unsignedAgreement")
      ? true
      : isApiStackUser;

  const getCibilPdfReport = async () => {
    const response = (await LoanApplicationService.getCibilPdfReport({
      userID: loanDetailsData?.userID!,
      token: fetchToken(),
      pageName
    })) as any;
    if (response?.status) {
      window.open(response.data?.cibilReport, "_blank");
      notify({
        title: "Success",
        type: "success",
        text: "Successfully downloaded CIBIL Report"
      });
    } else {
      notify({
        title: "Error",
        text: response.error
      });
    }
  };

  const getCIBILReportsExcel = async () => {
    const response = (await LoanApplicationService.getCIBILReportsExcel({
      userID: loanDetailsData?.userID!,
      token: fetchToken(),
      pageName
    })) as any;
    if (response?.status) {
      notify({
        title: "Success",
        type: "success",
        text: "Successfully downloaded Bureau Report"
      });

      window.open(response.data?.zipUrl, "_blank");
    } else {
      notify({
        title: "Error",
        type: "error",
        text: response.error
      });
    }
  };

  const sendLinkToUser = useCallback(async () => {
    const response = (await LoanApplicationService.sendLinkToUser({
      userID: loanDetailsData?.userID ?? "",
      token: fetchToken(),
      pageName,
      sourceEntityID: sourceEntityID
    })) as any;

    if (response?.status) {
      notify({
        title: "Success",
        type: "success",
        text: "Link Successfully Sent"
      });
    } else {
      notify({
        title: "Error",
        type: "error",
        text: response.error || "Link Sending Failed"
      });
    }
  }, [loanDetailsData?.userID, fetchToken, pageName, sourceEntityID]);

  const actionItems = useMemo(
    () => [
      {
        value: (
          <PrivateComponent
            requiredPage={pageName}
            sourceEntityId={sourceEntityID}
            requiredFunctions={[ACTIONS_LIST.SEND_AGREEMENT_TO_LENDER]}
          >
            <ActionButton
              title="Send Agreement to Lender"
              onClick={() => setShowSendSignAgreementModal(true)}
              icon={<DocumentCheckIcon className="h-4 w-4 mr-2 font-bold" />}
            />
          </PrivateComponent>
        ),
        isShow: isActionVisible(
          SIDEBAR_BUTTON_KEYS.SEND_AGREEMENT_TO_LENDER,
          canSendSignAgreementToLender
        )
      },
      {
        value: (
          <PrivateComponent
            requiredPage={pageName}
            sourceEntityId={sourceEntityID}
            requiredFunctions={[ACTIONS_LIST.RESUME_JOURNEY]}
          >
            <ActionButton
              title="Resume Journey"
              onClick={async () => {
                await generateLink();
              }}
              icon={<ArrowPathIcon className="h-4 w-4 mr-2 font-bold" />}
            />
          </PrivateComponent>
        ),
        isShow: isActionVisible(
          SIDEBAR_BUTTON_KEYS.RESUME_JOURNEY,
          visibleActionKeys?.includes("resumeJourney") ? true : isApiStackUser
        )
      },
      {
        value: (
          <ActionButton
            title="Move Application"
            onClick={() => {
              setShowMoveApplicationToNextStage(true);

              trackUserAction("move_application_to_next_stage", {
                loanApplicationID: loanID
              });
            }}
            icon={<ApproveLoanIcon className="w-4 h-4 mr-2" />}
          />
        ),
        isShow: checkSidebarButtonVisibility(
          SIDEBAR_BUTTON_KEYS.MOVE_APPLICATION
        )
      },
      {
        value: (
          <PrivateComponent
            requiredPage={pageName}
            sourceEntityId={sourceEntityID}
            requiredFunctions={[ACTIONS_LIST.APPROVE_KYC]}
          >
            <ActionButton
              title="Approve KYC"
              onClick={() => setShowApproveKYCModal(true)}
              icon={<ApproveLoanIcon className="w-4 h-4 mr-2" />}
            />
          </PrivateComponent>
        ),
        isShow: isActionVisible(
          SIDEBAR_BUTTON_KEYS.APPROVE_KYC,
          isAllowedToApproveKYC
        )
      },
      {
        value: (
          <PrivateComponent
            requiredPage={pageName}
            sourceEntityId={sourceEntityID}
            requiredFunctions={[ACTIONS_LIST.SEND_SESSION_LINK_TO_USER]}
          >
            <ActionButton
              title="Send Session Link to User"
              onClick={() => {
                sendLinkToUser();
              }}
              icon={<PaperAirplaneIcon className="w-4 h-4 mr-2" />}
            />
          </PrivateComponent>
        ),
        isShow: isActionVisible(SIDEBAR_BUTTON_KEYS.SEND_SESSION_LINK, true)
      },
      // This feature was implemented for Khatabook. Since they are no longer a customer, this is commented out
      // {
      //   value: (
      //     <PrivateComponent
      //       requiredPage={pageName}
      //       requiredFunctions={[ACTIONS_LIST.APPROVE_LOAN]}
      //     >
      //       <ActionButton onClick={() => {}} title={"Approve Loan"} />
      //     </PrivateComponent>
      //   ),
      //   isShow: isApproveLoan,
      // },
      {
        value: (
          <PrivateComponent
            requiredPage={pageName}
            sourceEntityId={sourceEntityID}
            requiredFunctions={[ACTIONS_LIST.REJECT_LOAN]}
          >
            <ActionButton
              title="Reject Loan"
              onClick={() => setShowDisqualifyLoanModal(true)}
              icon={<RejectLoanIcon className="w-4 h-4 mr-2" />}
            />
          </PrivateComponent>
        ),
        isShow: isActionVisible(SIDEBAR_BUTTON_KEYS.REJECT_LOAN, isRejectLoan)
      },
      {
        value: (
          <PrivateComponent
            requiredPage={pageName}
            sourceEntityId={sourceEntityID}
            requiredFunctions={[ACTIONS_LIST.REVOKE_DELETE_REQUEST]}
          >
            <ActionButton
              onClick={revokeDeleteRequest}
              title="Revoke Delete Request"
              icon={<BackwardIcon className="h-4 w-4 mr-2 font-bold" />}
            />
          </PrivateComponent>
        ),
        isShow: isActionVisible(
          SIDEBAR_BUTTON_KEYS.REVOKE_DELETE_REQUEST,
          isApiStackUser
        )
      },
      {
        value: (
          <PrivateComponent
            requiredPage={pageName}
            sourceEntityId={sourceEntityID}
            requiredFunctions={[ACTIONS_LIST.CANCEL_APPLICATION]}
          >
            <ActionButton
              title="Cancel Application"
              onClick={() => isMflLap ? cancelApplication() : setShowCancelLoanApplicationModal(true)}
              icon={<CancelApplicationIcon className="w-4 h-4 mr-2" />}
            />
          </PrivateComponent>
        ),
        isShow: isActionVisible(
          SIDEBAR_BUTTON_KEYS.CANCEL_APPLICATION,
          canCancelApplication
        )
      },
      {
        value: (
          <PrivateComponent
            requiredPage={pageName}
            sourceEntityId={sourceEntityID}
            requiredFunctions={[ACTIONS_LIST.ADD_OFFER_NEGOTIATION]}
          >
            <ActionButton
              title="Initiate Loan Deviation"
              onClick={() => {
                currentActiveSliderHandler("INITIATE");
              }}
              icon={<ArrowRightStartOnRectangleIcon className="w-4 h-4 mr-2" />}
              isDisabled={!loanTermDeviationStatus?.allowOfferWFInit}
            />
          </PrivateComponent>
        ),
        isShow: isActionVisible(
          SIDEBAR_BUTTON_KEYS.INITIATE_LOAN_DEVIATION,
          loanDetailsData?.status != "UNDER_REVIEW" &&
          loanTermDeviationStatus?.allowOfferWFInit
        )
      },
      {
        value: (
          <PrivateComponent
            requiredPage={pageName!}
            requiredFunctions={[ACTIONS_LIST.APPROVE_BUSINESS_DOCS]}
            sourceEntityId={sourceEntityID}
          >
            <ActionButton
              title={"Approve Business Details"}
              onClick={() => setShowConfirmDialog(true)}
            />
          </PrivateComponent>
        ),
        isShow: isActionVisible(
          SIDEBAR_BUTTON_KEYS.APPROVE_BUSINESS_DETAILS,
          showApproveBusinessKYC && isApiStackUser
        )
      }
    ],
    [
      pageName,
      sourceEntityID,
      canSendSignAgreementToLender,
      visibleActionKeys,
      isApiStackUser,
      generateLink,
      setShowMoveApplicationToNextStage,
      trackUserAction,
      loanID,
      checkSidebarButtonVisibility,
      setShowApproveKYCModal,
      isAllowedToApproveKYC,
      sendLinkToUser,
      setShowDisqualifyLoanModal,
      isRejectLoan,
      revokeDeleteRequest,
      setShowCancelLoanApplicationModal,
      canCancelApplication,
      currentActiveSliderHandler,
      loanTermDeviationStatus?.allowOfferWFInit,
      loanDetailsData?.status,
      setShowConfirmDialog,
      showApproveBusinessKYC,
      setShowSendSignAgreementModal,
      isActionVisible
    ]
  );

  // Create a stable signature of actionItems that only includes meaningful data (isShow values)
  const actionItemsSignature = useMemo(() => {
    return actionItems.map((item) => item.isShow).join(",");
  }, [actionItems]);

  // Use ref to track previous signature and only update when it actually changes
  const prevActionItemsSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    // Only update if the signature (isShow values) actually changed
    if (
      actionItems &&
      prevActionItemsSignatureRef.current !== actionItemsSignature
    ) {
      prevActionItemsSignatureRef.current = actionItemsSignature;

      setLoanData((prev) =>
        prev
          ? {
            ...prev,
            actions: actionItems
          }
          : prev
      );
    }
  }, [actionItems, actionItemsSignature, setLoanData]);

  // Update navbar with DetailsSidebar data
  useEffect(() => {
    if (loanDetailsData && loanDetails) {
      const sidebarData = loanDetailSidebarFields.map((field) => {
        return {
          name: loanDetailFields?.[field]?.label ?? "",
          value: loanDetails?.[field] || loanDetailsData?.[field] || "-",
          key: field
        };
      });

      setLoanData((prev) =>
        prev
          ? {
            ...prev,
            detailsSidebarData: {
              details: {
                ...loanDetailsData,
                ...loanDetailsData?.loanDetails
              },
              selfie: loanDetailsData?.selfie,
              config:
                LOAN_STATUS_CONFIG?.[
                (isMflLap
                  ? loanDetailsData?.workflowState
                  : loanDetailsData?.status) as keyof typeof LOAN_STATUS_CONFIG
                ],
              items: prependBankConnectItem(sidebarData),
              isMFLLAP: isMflLap
            }
          }
          : prev
      );
    }
  }, [loanDetailsData, loanDetails, isMflLap, setLoanData, bcStatus]);

  const docActions = [
    {
      value: (
        <PrivateComponent
          requiredPage={pageName}
          sourceEntityId={sourceEntityID}
          requiredFunctions={[ACTIONS_LIST.DOWNLOAD_AUTHENTICATION_LETTER]}
        >
          <ActionButton
            title="Auth Letter"
            onClick={async () => {
              await getAuthLetter();
            }}
            icon={<ArrowDownTrayIcon className="w-4 h-4 mr-2 text-inherit" />}
          />
        </PrivateComponent>
      ),
      isShow: isActionVisible(
        SIDEBAR_BUTTON_KEYS.DOWNLOAD_AUTH_LETTER,
        canDownloadAuthLetter
      )
    },
    {
      value: (
        <PrivateComponent
          requiredPage={pageName}
          sourceEntityId={sourceEntityID}
          requiredFunctions={[ACTIONS_LIST.DOWNLOAD_SIGNED_AGREEMENT]}
        >
          <ActionButton
            title="Signed Agreement"
            onClick={async () => {
              await getSignedAgreement();
            }}
            icon={<ArrowDownTrayIcon className="w-4 h-4 mr-2 text-inherit" />}
          />
        </PrivateComponent>
      ),
      isShow: isActionVisible(
        SIDEBAR_BUTTON_KEYS.DOWNLOAD_SIGNED_AGREEMENT,
        canDownloadSignAgreement
      )
    },
    {
      value: (
        <PrivateComponent
          requiredPage={pageName}
          sourceEntityId={sourceEntityID}
          requiredFunctions={[ACTIONS_LIST.DOWNLOAD_STATEMENT_OF_ACCOUNTS]}
        >
          <ActionButton
            title="Statement of Accounts"
            onClick={async () => {
              await getSoA();
            }}
            icon={<ArrowDownTrayIcon className="w-4 h-4 mr-2 text-inherit" />}
          />
        </PrivateComponent>
      ),
      isShow: isActionVisible(
        SIDEBAR_BUTTON_KEYS.DOWNLOAD_STATEMENT_OF_ACCOUNTS,
        canGetSoA && visibleActionKeys?.includes("statementOfAccounts")
          ? true
          : isApiStackUser
      )
    },
    {
      value: (
        <PrivateComponent
          requiredPage={pageName}
          sourceEntityId={sourceEntityID}
          requiredFunctions={[ACTIONS_LIST.DOWNLOAD_UNSIGNED_AGREEMENT]}
        >
          <ActionButton
            title="Unsigned Agreement"
            onClick={async () => {
              await getUnsignedAgreement();
            }}
            icon={<ArrowDownTrayIcon className="w-4 h-4 mr-2 text-inherit" />}
          />
        </PrivateComponent>
      ),
      isShow: isActionVisible(
        SIDEBAR_BUTTON_KEYS.DOWNLOAD_UNSIGNED_AGREEMENT,
        showUnsignedAgreement
      )
    },
    {
      value: (
        <PrivateComponent
          requiredPage={pageName}
          sourceEntityId={sourceEntityID}
          requiredFunctions={[ACTIONS_LIST.DOWNLOAD_CIBIL_PDF_REPORT]}
        >
          <ActionButton
            title="Download CIBIL"
            onClick={getCibilPdfReport}
            icon={<ArrowDownTrayIcon className="w-4 h-4 mr-2 text-inherit" />}
          />
        </PrivateComponent>
      ),
      isShow: isActionVisible(SIDEBAR_BUTTON_KEYS.DOWNLOAD_CIBIL_PDF_REPORT, true)
    },
    {
      value: (
        <PrivateComponent
          requiredPage={pageName}
          sourceEntityId={sourceEntityID}
          requiredFunctions={[ACTIONS_LIST.DOWNLOAD_CIBIL_EXCEL_REPORT]}
        >
          <ActionButton
            title="Download Bureau Report"
            onClick={getCIBILReportsExcel}
            icon={<ArrowDownTrayIcon className="w-4 h-4 mr-2 text-inherit" />}
          />
        </PrivateComponent>
      ),
      isShow: isActionVisible(
        SIDEBAR_BUTTON_KEYS.DOWNLOAD_CIBIL_EXCEL_REPORT,
        true
      )
    }
  ];

  const cancelApplication = async () => {
    const response = (await LoanApplicationServiceUnified.cancelUser({
      token: fetchToken(),
      pageName,
      payload: {
        mobile: loanDetails?.mobile || "",
        sourceEntityID: sourceEntityID || "",
        userID: loanDetailsData?.userID
      },
      isLenderDashboard: false,
    })) as any;

    if (response?.status) {
      notify({
        type: "success",
        title: "Success",
        text: "Application cancelled successfully.",
      });
    } else {
      notify({
        title: "Error",
        type: 'error',
        text: response?.error || "Failed to cancel application.",
      });
    }

    reloadLoanDetails();
  };



  let sidebarItems = loanDetailSidebarFields.map((field) => {
    return {
      name: loanDetailFields?.[field]?.label ?? "",

      value: loanDetails?.[field] || loanDetailsData?.[field] || "-",
      key: field
    };
  });
  sidebarItems = prependBankConnectItem(sidebarItems);

  const breadcrumbs: Array<BreadCrumb> = [
    { name: "Loan Applications", href: isConfigDrivenTabs ? "/my-queue" : "/loan-applications", current: false },
    {
      name: isMobile
        ? loanDetailsData?.loanApplicationID
        : loanDetailsData?.loanDetails?.name ||
        loanDetailsData?.loanApplicationID,
      href: `/loan-applications/details/${sourceEntityID}/${loanID}`,
      current: false
    }
  ];

  const pan4char = loanDetailsData?.loanDetails?.pan?.charAt(3);
  const isPartnerTab =
    pan4char === "F" && loanDetailsData?.constitution === "Others";
  const isDirectorTab =
    pan4char === "C" && loanDetailsData?.constitution === "Others";

  const isCoApp =
    loanDetailsData?.constitution === "Proprietorship"
      ? true
      : loanDetailsData?.constitution === "Others" &&
      !["F", "C"].includes(pan4char);

  const removeCoApp = ["Private Limited", "Partnership"].includes(
    loanDetailsData?.constitution
  );

  // MFL LAP: Always show Co-applicant tab, never exclude it
  const excludedHref = isMflLap
    ? "/directors" // For MFL LAP, never exclude co-applicants, only exclude directors
    : (removeCoApp || !isDirectorTab || !isPartnerTab) && !isCoApp
      ? "/co-applicants"
      : "/directors";

  let tabs = getTabsByProductType(
    "loan_application",
    loanDetailsData?.loanType,
    permissions,
    pageConfig?.[pageName]?.tabs,
    userState?.organizationID
  )?.filter((tab) => tab?.href !== excludedHref);

  if (loanDetailsData?.constitution === "Partnership" || isPartnerTab) {
    tabs = tabs?.map((list) => {
      if (list?.href === "/directors") {
        return {
          ...list,
          name: "Partners"
        };
      }
      return list;
    });
  }

  if (userState?.hideKYC) {
    // Currently condition will return true only for Tata Group
    tabs = tabs.filter((tab) => tab.name !== "KYC");
  }

  const showTaskStatus =
    pageConfig?.[pageName ?? ""]?.hideKeys?.includes("showTaskStatus");


  const isHFCL = useIsHFCL(false, userState);

  const getConfig = () => {
    let config;

    if (isConfigDrivenTabContent || isHFCL) {
      config = LOAN_WORKFLOW_STATE_CONFIG[loanDetailsData?.workflowState];
      return config;
    }

    config =
      LOAN_STATUS_CONFIG?.[
      showTaskStatus
        ? loanDetailsData?.workflowState
        : loanDetailsData?.status
      ];

    if (showTaskStatus && !config) {
      const tempConfig = LOAN_STATUS_CONFIG["PRE_AGREEMENT"];
      tempConfig.label = loanDetailsData?.workflowState;

      config = tempConfig;
    }

    return config;
  };

  const getTabs = () => {
    if (isConfigDrivenTabs) {
      return derivedTabs;
    }

    if (isMflLap) {
      return Array.isArray(tabsAndSidebarConfig?.tabs)
        ? tabsAndSidebarConfig.tabs
        : [];
    }

    return tabs;
  };

  return (
    <>
      <div className="min-h-full">
        {!isMflLap && (
          <PageHeader
            title=""
            breadcrumbs={breadcrumbs}
            isMyQueueHomePage={
              isMyQueueEnabledOrg(loanDetailsData?.organizationID) ||
              isConfigDrivenTabContent
            }
          />
        )}

        <WaitStateBanner
          waitStateData={waitStateData}
          waitLoading={waitLoading}
          resolveWaitState={resolveWaitState}
        />

        {status ? (
          <UiConfigGate isLoading={tabsFromUIConfig.isLoading}>
            <main className="py-0 md:pt-6 md:pb-3">
              <div className="mx-auto max-w-3xl lg:max-w-full flex items-start justify-between md:space-x-2">
                <DetailsSidebar
                  items={sidebarItems}
                  actions={actionItems}
                  docActions={docActions}
                  status={loanDetailsData?.status}
                  selfie={loanDetailsData?.selfie}
                  config={getConfig()}
                  isMFLLAP={isMflLap}
                  details={{
                    ...loanDetailsData,
                    ...loanDetailsData?.loanDetails
                  }}
                />

                <div className="w-full md:w-[80%]">
                  <section aria-labelledby="applicant-information-title">
                    <div className="bg-white md:rounded-lg md:border md:border-gray-200">
                      <div className="rounded-bl-none rounded-br-none px-2 md:px-0 md:block">
                        <TabsOne
                          tabs={getTabs() || []}
                          hasScroll={true}
                          styles={{
                            tab: "min-w-max px-8"
                          }}
                          baseURL={`/loan-applications/details/${sourceEntityID}/${loanID}`}
                        />
                      </div>
                      <div className="md:border-t md:border-gray-200 px-5 md:px-4 py-2 md:py-5 lg:4 h-600 overflow-auto">
                        {(() => {
                          const outletContext = {
                            hasMorePages: false,
                            activeTab: activeTab,
                            loanDetails: {
                              ...loanDetails,
                              isLanUpdated: loanDetailsData?.isLanUpdated,
                              ruleEnginePredictors:
                                loanDetailsData?.ruleEnginePredictors ?? [],
                              coApplicantDetails:
                                loanDetailsData?.coApplicantDetails,
                              loanAccountNumber:
                                loanDetailsData?.loanAccountNumber,
                              createdAt: loanDetailsData?.createdAt,
                              updatedAt: loanDetailsData?.updatedAt,
                              cibilScore:
                                loanDetails?.bureauScores.find(
                                  (b: any) => b.bureauName === "CIBIL"
                                )?.bureauScore ?? -1,
                              cibilStatus:
                                loanDetails?.bureauScores.find(
                                  (b: any) => b.bureauName === "CIBIL"
                                )?.bureauStatus ?? "",
                              loanApplicationNum:
                                loanDetailsData?.loanApplicationNum,
                              sourceEntityName: loanDetailsData?.sourceEntityName,
                              loanType: loanDetailsData?.loanType,
                              userID: loanDetailsData?.userID,
                              status: loanDetailsData?.status,
                              isDirectTxnEnabled:
                                loanDetailsData?.isDirectTxnEnabled,
                              directTransactionFields:
                                loanDetailsData?.directTransactionFields,
                              base: loanDetails?.base,
                              offerType: loanDetailsData?.offerType,
                              offerAmount: loanDetailsData?.offerAmount,
                              programName: loanDetailsData?.programName ?? "",
                              reqLoanAmount:
                                loanDetailsData?.reqLoanAmount ?? null,
                              processingFee:
                                loanDetailsData?.processingFee ?? null,
                              utmSource: loanDetailsData?.utmSource ?? "",
                              utmContent: loanDetailsData?.utmContent ?? "",
                              utmMedium: loanDetailsData?.utmMedium ?? "",
                              utmCampaign: loanDetailsData?.utmCampaign ?? "",
                              campaignType: loanDetailsData?.campaignType ?? "",
                              directorDetails:
                                loanDetailsData?.directorDetails ?? [],
                              loanVariant: loanDetailsData?.loanVariant,
                              constitution: loanDetailsData?.constitution,
                              organizationID: loanDetailsData?.organizationID,
                              lenderID: loanDetailsData?.lenderID,
                              loanApplicationID: loanDetailsData?.loanApplicationID,
                              workflowInstanceID: loanDetailsData?.workflowInstanceID,
                              program: loanDetailsData?.program ?? "",
                              productType: loanOfferDetails?.[0]?.productType ?? "",
                              subProduct: underscoreToSpace(
                                loanOfferDetails?.[0]?.subProduct
                              )
                            },
                            workflowLoaderData: workflowData,
                            reloadWorkflowDetails,
                            reloadLoanDetails,
                            loanOfferDetails: loanOfferDetails,
                            proposal: {
                              isSliderOpen,
                              setIsSliderOpen,
                              refetchLoanTerm,
                              currentActiveSlider,
                              loanTermDeviationStatus,
                              currentActiveSliderHandler
                            },
                            whiteLabelStyle,
                            uiPermissions: tabsAndSidebarConfig?.uiPermissions
                          }

                          return (
                            <>
                              {(isConfigDrivenTabContent || activeTab?.rendererType === "ui_config_v1") ? (
                                <DynamicTabRenderer
                                  whiteLabelStyle={whiteLabelStyle}
                                  activeTab={activeTab}
                                  loanDetails={loanDetails}
                                  loanDetailsData={loanDetailsData}
                                  isLenderDashboard={true}
                                  notify={notify}
                                  token={fetchToken()}
                                  useLocation={useLocation}
                                  useNavigate={useNavigate}
                                  GetMixpanelContext={GetMixpanelContext}
                                />
                              ) : (
                                <Outlet context={outletContext} />
                              )}
                            </>
                          )
                        })()}
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </main>
          </UiConfigGate>
        ) : (
          <p className="text-sm py-12">Loan Application not found!</p>
        )}
      </div>

      <FloatingNotesButton
        whiteLabelStyle={whiteLabelStyle}
        onClick={() => setIsNotesOpen(true)}
      />

      {status ? (
        <>
          <CollapsibleSidebarUnified
            show={isNotesOpen}
            setShow={setIsNotesOpen}
            style={{
              container:
                window.innerWidth < 500
                  ? "w-full !p-0"
                  : "min-w-[500px] !w-[25vw] !p-0"
            }}
          >
            <Notes
              notes={notes}
              key={isNotesOpen}
              onSubmit={onAddNote}
              close={() => setIsNotesOpen(false)}
              isLenderPlatform={false}
            />
          </CollapsibleSidebarUnified>

          <DialogOne
            title="Resume Journey"
            open={isGenerateLinkModalOpen}
            setOpen={setIsGenerateLinkModalOpen}
            description="Use this link to resume loan journey"
          >
            <GenerateLinkModal
              webLink={webLink}
              setOpen={setIsGenerateLinkModalOpen}
            />
          </DialogOne>

          <DialogOne
            title="Move Application"
            open={showMoveApplicationToNextStage}
            setOpen={setShowMoveApplicationToNextStage}
          >
            <NextStage
              notify={notify}
              taskID={loanDetailsData?.taskID}
              workflowInstanceID={loanDetailsData?.workflowInstanceID}
              pageName={pageName}
              token={fetchToken()}
              reload={() => {
                reloadLoanDetails();
                window.location.reload();
              }}
              loanApplicationID={loanID as string}
              setOpen={setShowMoveApplicationToNextStage}
              isLenderDashboard={false}
              setShowCaseMovedModal={setShowCaseMovedModal}
            />
          </DialogOne>

          <DialogOne
            open={showCaseMovedModal}
            title="Case Moved"
            setOpen={() => {
              window.location.reload();
              setShowCaseMovedModal(false);
            }}
          >
            <CaseMovedModal
              closeModal={() => {
                window.location.reload();
                setShowCaseMovedModal(false);
              }}
            />
          </DialogOne>

          <DialogOne
            description=""
            open={showApproveKYCModal}
            setOpen={setShowApproveKYCModal}
            title={`Approve KYC for ${loanDetails.name}?`}
          >
            <ApproveKYCModal
              pageName={pageName}
              setOpen={setShowApproveKYCModal}
              reload={() => reloadLoanDetails()}
              loanApplicationID={loanID as string}
            />
          </DialogOne>

          <DialogOne
            description=""
            title="Disqualify User?"
            open={showDisqualifyLoanModal}
            setOpen={setShowDisqualifyLoanModal}
          >
            <DisqualifyLoanApplicationModal
              reload={() => reloadLoanDetails()}
              loanApplicationID={loanID as string}
              setOpen={setShowDisqualifyLoanModal}
            />
          </DialogOne>

          <DialogOne
            description=""
            title="Cancel Application"
            open={showCancelLoanApplicationModal}
            setOpen={setShowCancelLoanApplicationModal}
          >
            <CancelLoanApplicationModal
              pageName={pageName}
              reload={() => reloadLoanDetails()}
              loanApplicationID={loanID as string}
              setOpen={setShowCancelLoanApplicationModal}
            />
          </DialogOne>

          <DialogOne
            open={showSendSignAgreementModal}
            title="Send Sign Agreement to Lender"
            setOpen={setShowSendSignAgreementModal}
          >
            <SendSignAgreementModal
              pageName={pageName}
              reload={() => reloadLoanDetails()}
              loanApplicationID={loanID as string}
              setOpen={setShowSendSignAgreementModal}
            />
          </DialogOne>

          <CollapsibleSidebar show={isSliderOpen} setShow={setIsSliderOpen}>
            <SliderContainer
              pageName={pageName}
              sliderBack={sliderBack}
              isSliderOpen={isSliderOpen}
              timelineInfo={timelineInfo}
              setSliderBack={setSliderBack}
              setIsSliderOpen={setIsSliderOpen}
              loanOfferDetails={loanOfferDetails}
              currentActiveSlider={currentActiveSlider}
              setCurrentSlider={currentActiveSliderHandler}
              loanDeviationPageName={loanDeviationPageName}
              isLenderDashboard={isLoanDeviationLender}
              loanDeviationInfo={loanDeviationInfo}
              sourceEntityID={sourceEntityID}
              isABFLBL={isABFLBL}
              isABFLPL={isABFLPL}
            />
          </CollapsibleSidebar>

          <DialogOne
            open={showConfirmDialog}
            setOpen={setShowConfirmDialog}
            title={"Confirm"}
          >
            <ConfirmationModal
              cancelButtontext="Cancel"
              onConfirm={() => approveBusinessKYC()}
              onCancel={() => {
                setShowConfirmDialog(false);
              }}
              confirmButtonText={"Confirm"}
            >
              <p className="text-sm mb-4">
                Are you sure you want to approve business details ?
              </p>
            </ConfirmationModal>
          </DialogOne>
        </>
      ) : null}
    </>
  );
}

export default LoanApplicationDetails;
